/**
 * Auth context — wraps Firebase auth state and exposes the current user,
 * verification status, and admin flag to the app + router guards (CLAUDE.md §7).
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthChange, type User } from "../lib/firebase";

interface AuthState {
  user: User | null;
  loading: boolean;
  isVerified: boolean;
  isAdmin: boolean;
  /** Force a re-read of auth state (e.g. after email verification). */
  refresh: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const unsub = onAuthChange(async (u) => {
      setUser(u);
      if (u) {
        try {
          const result = await u.getIdTokenResult();
          setIsAdmin(result.claims.admin === true);
        } catch {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsub;
  }, [version]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isVerified: !!user?.emailVerified,
      isAdmin,
      refresh: () => setVersion((v) => v + 1),
    }),
    [user, loading, isAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
